import type {
  RatingWithColor,
  RatingWithColorAndLikelihoodImpact,
} from '@risksmart-app/components/src/hooks/types';
import { useCallback, useMemo } from 'react';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';
import type { RiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetLatestRiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetRiskAssessmentResultConfigByIds } from '@/hooks/queries/risk-assessment-result-config/useGetRiskAssessmentResultConfigByIds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export interface MatrixCell {
  label: string;
  value: number;
  color: string;
  likelihood: number;
  impact: number;
}

const toRatingOptions = (
  matrix: RiskAssessmentResultConfig['matrix']
): RatingWithColorAndLikelihoodImpact<number>[] =>
  matrix.map((entry) => ({
    label: entry.title,
    value: entry.value,
    color: entry.color,
    likelihoodImpact: [{ likelihood: entry.likelihood, impact: entry.impact }],
    range: [0, 0] as readonly [number, number],
  }));

const toLikelihoodImpactOptions = (
  ratings: { title: string; value: number; color: string }[]
): RatingWithColor<number>[] =>
  ratings.map((r) => ({
    label: r.title,
    value: r.value,
    color: r.color,
  }));

const matrixKey = (likelihood: number, impact: number) =>
  `${likelihood}:${impact}`;

const buildMatrixLookup = (
  matrix: RiskAssessmentResultConfig['matrix']
): Map<string, MatrixCell> => {
  const map = new Map<string, MatrixCell>();
  for (const entry of matrix) {
    map.set(matrixKey(entry.likelihood, entry.impact), {
      label: entry.title,
      value: entry.value,
      color: entry.color,
      likelihood: entry.likelihood,
      impact: entry.impact,
    });
  }

  return map;
};

const buildValueLookup = (
  ratings: { title: string; value: number; color: string }[]
): Map<number, RatingWithColor<number>> => {
  const map = new Map<number, RatingWithColor<number>>();
  for (const r of ratings) {
    map.set(r.value, { label: r.title, value: r.value, color: r.color });
  }

  return map;
};

// Value-deduped rating options, ascending — many matrix cells share a value, so
// keep the first occurrence.
const buildRatingOptionsFromMatrix = (
  matrix: RiskAssessmentResultConfig['matrix']
): RatingWithColor<number>[] => {
  const seen = new Map<number, RatingWithColor<number>>();
  for (const entry of matrix) {
    if (!seen.has(entry.value)) {
      seen.set(entry.value, {
        label: entry.title,
        value: entry.value,
        color: entry.color,
      });
    }
  }

  return Array.from(seen.values()).sort((a, b) => a.value - b.value);
};

// Dedupe by rating value, keeping the first occurrence (many cells share one).
const buildRatingByValueLookup = (
  matrix: RiskAssessmentResultConfig['matrix']
): Map<number, RatingWithColor<number>> => {
  const map = new Map<number, RatingWithColor<number>>();
  for (const entry of matrix) {
    if (!map.has(entry.value)) {
      map.set(entry.value, {
        label: entry.title,
        value: entry.value,
        color: entry.color,
      });
    }
  }

  return map;
};

interface ConfigLookups {
  matrixLookup: Map<string, MatrixCell>;
  likelihoodByValue: Map<number, RatingWithColor<number>>;
  impactByValue: Map<number, RatingWithColor<number>>;
  ratingByValue: Map<number, RatingWithColor<number>>;
}

const buildConfigLookups = (
  config: RiskAssessmentResultConfig
): ConfigLookups => ({
  matrixLookup: buildMatrixLookup(config.matrix),
  likelihoodByValue: buildValueLookup(config.likelihood.ratings),
  impactByValue: buildValueLookup(config.impact.ratings),
  ratingByValue: buildRatingByValueLookup(config.matrix),
});

export const useScoringSettings = (options: {
  configType: ConfigType;
  configIds?: string[];
  skip?: boolean;
}) => {
  const scoringSettingsDataEnabled = useIsFeatureFlagEnabled(
    'scoring_settings_data'
  );
  const skipQueries = !scoringSettingsDataEnabled || options.skip === true;
  const {
    config: rawConfig,
    loading,
    id: latestConfigId,
  } = useGetLatestRiskAssessmentResultConfig({
    type: options.configType,
    skip: skipQueries,
  });
  const latestConfig = scoringSettingsDataEnabled ? rawConfig : undefined;
  const hasScoringSettings = !!latestConfig;

  // The latest config is already seeded into lookupCache below, so exclude it
  // from the by-id fetch (which is cache-and-network and would otherwise
  // refetch it). Only historical config versions need fetching.
  const historicalConfigIds = useMemo(
    () => (options.configIds ?? []).filter((id) => id !== latestConfigId),
    [options.configIds, latestConfigId]
  );

  const { configById, loading: configsByIdLoading } =
    useGetRiskAssessmentResultConfigByIds(historicalConfigIds, {
      skip: skipQueries,
    });

  const latestLookups = useMemo(
    () =>
      latestConfig
        ? buildConfigLookups(latestConfig)
        : {
            matrixLookup: new Map<string, MatrixCell>(),
            likelihoodByValue: new Map<number, RatingWithColor<number>>(),
            impactByValue: new Map<number, RatingWithColor<number>>(),
            ratingByValue: new Map<number, RatingWithColor<number>>(),
          },
    [latestConfig]
  );

  const lookupCache = useMemo(() => {
    const cache = new Map<string, ConfigLookups>();
    if (latestConfigId && latestConfig) {
      cache.set(latestConfigId, latestLookups);
    }
    configById.forEach((c, id) => {
      cache.set(id, buildConfigLookups(c));
    });

    return cache;
  }, [configById, latestConfigId, latestConfig, latestLookups]);

  const getLookupsForConfig = useCallback(
    (configId: string | null | undefined): ConfigLookups | undefined => {
      if (configId) {
        const cached = lookupCache.get(configId);
        if (cached) {
          return cached;
        }

        // The version is associated with the rating but not yet cached. While
        // that fetch is in flight, withhold rather than flash the latest
        // matrix's labels/colours. Once it has settled and the id still isn't
        // present (e.g. a config that was never fetched), fall back to latest so
        // a genuinely-rated record still resolves instead of showing "Unrated".
        return configsByIdLoading ? undefined : latestLookups;
      }

      return latestLookups;
    },
    [lookupCache, latestLookups, configsByIdLoading]
  );

  const getRatingByLikelihoodAndImpact = useCallback(
    (
      likelihood: number,
      impact: number,
      configId?: string | null
    ): MatrixCell | undefined =>
      getLookupsForConfig(configId)?.matrixLookup.get(
        matrixKey(likelihood, impact)
      ),
    [getLookupsForConfig]
  );

  const getLikelihoodByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.likelihoodByValue.get(value),
    [getLookupsForConfig]
  );

  const getImpactByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.impactByValue.get(value),
    [getLookupsForConfig]
  );

  // Resolve a rating value to its band for a specific config version.
  const getRiskRatingByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.ratingByValue.get(value),
    [getLookupsForConfig]
  );

  const riskRatingOptions = useMemo<RatingWithColor<number>[]>(
    () =>
      latestConfig ? buildRatingOptionsFromMatrix(latestConfig.matrix) : [],
    [latestConfig]
  );

  // Resolve a config version by id. An absent or latest id resolves to the
  // latest config. A historical id resolves to its loaded config; while that
  // fetch is in flight, withhold (undefined) rather than flash the latest
  // version, falling back to latest only once it has settled.
  const getConfigByVersion = useCallback(
    (configId?: string | null): RiskAssessmentResultConfig | undefined => {
      if (!configId || configId === latestConfigId) {
        return latestConfig;
      }

      const loaded = configById.get(configId);
      if (loaded) {
        return loaded;
      }

      return configsByIdLoading ? undefined : latestConfig;
    },
    [configById, latestConfig, latestConfigId, configsByIdLoading]
  );

  // Dropdown options for a likelihood/impact/riskRating field, resolved against
  // a specific config version, so a read-only stale rating shows the labels and
  // colours it was assessed under.
  const getOptionsByCategory = useCallback(
    (
      category: 'likelihood' | 'impact' | 'riskRating',
      configId?: string | null
    ): RatingWithColor<number>[] => {
      const config = getConfigByVersion(configId);
      if (!config) {
        return [];
      }
      switch (category) {
        case 'likelihood':
          return toLikelihoodImpactOptions(config.likelihood.ratings);
        case 'impact':
          return toLikelihoodImpactOptions(config.impact.ratings);
        case 'riskRating':
          return buildRatingOptionsFromMatrix(config.matrix);
      }
    },
    [getConfigByVersion]
  );

  // Impact categories for a specific config version, so a stale multi-impact
  // rating maps its stored impacts onto the categories it was assessed under.
  const getImpactCategories = useCallback(
    (configId?: string | null) =>
      getConfigByVersion(configId)?.impact.categories ?? [],
    [getConfigByVersion]
  );

  const impactCategories = latestConfig?.impact.categories ?? [];
  const hasMultipleImpacts = hasScoringSettings && impactCategories.length >= 2;

  return {
    hasScoringSettings,
    hasMultipleImpacts,
    loading,
    latestConfigId,
    config: latestConfig,
    matrixOptions: latestConfig ? toRatingOptions(latestConfig.matrix) : [],
    likelihoodOptions: latestConfig
      ? toLikelihoodImpactOptions(latestConfig.likelihood.ratings)
      : [],
    impactOptions: latestConfig
      ? toLikelihoodImpactOptions(latestConfig.impact.ratings)
      : [],
    riskRatingOptions,
    impactCategories,
    getRatingByLikelihoodAndImpact,
    getLikelihoodByValue,
    getImpactByValue,
    getRiskRatingByValue,
    getOptionsByCategory,
    getImpactCategories,
  };
};
